import * as hardware from "@client/hardware";
import * as router from "@client/router";
import {PrimaryMetric, formatStatus} from "@client/templates/ClientDashboard";
import * as utils from "@client/utils";
import {server} from "@reactivated";
import React from "react";

export default function ClientTable(props: server.manager.templates.ClientDashboard) {
    return (
        <div className="-mx-4">
            <div className="flow-root">
                <div className="overflow-x-auto">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead>
                                <tr>
                                    <th
                                        scope="col"
                                        className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold whitespace-nowrap text-gray-900 sm:pl-0"
                                    >
                                        Name
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        colSpan={2}
                                    >
                                        {props.clients[0].primary_metric?.metric
                                            .label ?? "N/A"}
                                    </th>
                                    {props.clients[0].statuses.map((status, index) => (
                                        <th
                                            scope="col"
                                            key={index}
                                            className="px-2 py-3.5 text-sm font-semibold text-gray-900"
                                        >
                                            <div>
                                                {status.date != null &&
                                                    utils.formatShortMonthAndDay(
                                                        status.date,
                                                    )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {props.clients.map(({client_manager, ...snapshot}) => (
                                    <tr key={client_manager.id}>
                                        <td className="py-2 pr-3 pl-4 text-sm whitespace-nowrap sm:pl-0">
                                            <div className="font-medium text-gray-900">
                                                {client_manager.name}
                                            </div>
                                            <div className="-mt-0.5 text-gray-500">
                                                {utils.formatDistanceToNow(
                                                    snapshot.last_seen,
                                                )}{" "}
                                                ago
                                            </div>
                                        </td>
                                        <PrimaryMetric
                                            metric={snapshot.primary_metric}
                                            isImperial={
                                                client_manager.client.profile
                                                    .is_imperial
                                            }
                                        >
                                            <div />
                                        </PrimaryMetric>
                                        {snapshot.statuses.map((status, index) => (
                                            <td
                                                key={index}
                                                className="p-2 text-sm whitespace-nowrap text-gray-500"
                                            >
                                                {formatStatus(status.status)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export const Manager = router.Manager.withHooks(() => {
    return {};
})
    .withIndex((props) => {
        // The dashboard link embeds a short-lived sesame token minted when the
        // route resolved. Safari/Cordova require window.open to be synchronous
        // inside the click, so we can't mint a fresh one at click time. Instead
        // we re-mint it whenever the app returns to the foreground, keeping the
        // token fresh for whenever the coach actually taps.
        const [url, setUrl] = React.useState(props.resolved.manager.url);

        React.useEffect(() => {
            const refresh = async () => {
                const result = await server.core.rpc.get_client_dashboard_link();
                if (result.type === "success") {
                    setUrl(result.data);
                }
            };

            const listener = hardware.App.addListener("appStateChange", (appState) => {
                if (appState.isActive) {
                    void refresh();
                }
            });

            return () => {
                void (async () => {
                    void (await listener).remove();
                })();
            };
        }, []);

        return (
            <div className="space-y-4">
                <p className="mt-2 text-center sm:text-left">
                    This is a snapshot of your clients. Click below to open the full
                    dashboard in your browser.
                </p>
                <button
                    type="button"
                    onClick={() => {
                        if (window.cordova == null) {
                            window.location.href = url;
                        } else {
                            window.open(url);
                        }
                    }}
                    className="w-full rounded-sm bg-white px-2 py-1.5 text-xs font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50 sm:w-32"
                >
                    Dashboard
                </button>
                {props.resolved.manager.dashboard.props.clients.length > 0 && (
                    <ClientTable {...props.resolved.manager.dashboard.props} />
                )}
            </div>
        );
    })
    .withActions(() => [])
    .withOptions(() => ({title: "My Clients"}));
